import { isArray } from "lodash";
import { fromExactMatchQueryString } from "@/helpers/url";

export const PriorArtTypes = {
  PATENT: 'Patent',
  UPLOAD_FILE: 'UploadFile',
  MANUAL_ENTRY: 'ManualEntry',
  RECOMMENDATION: 'Recommendation',
  STANDARD: 'Standard',
};

export const PriorArtDocumentType = {
  GRANT: 'GRANT',
  APPLICATION: 'APPLICATION',
  INTERNATIONAL: 'INTERNATIONAL',
  OTHER: 'OTHER',
};

export const countryToLanguageCode = {
  US: 'EN', // English
  CA: 'EN', // English
  WO: 'EN', // English
  GB: 'EN', // English
  EP: 'EN', // English (default for EP), could also be 'FR' or 'DE'
  KR: 'KO', // Korean
  JP: 'JA', // Japanese
  CN: 'ZH', // Chinese
  DE: 'DE', // German
  FR: 'FR', // French
  DEFAULT: 'EN', // Default language if country code is not found
};
/**
 * Consolidated entity-type labels.
 *
 * One vocabulary for all four annotation fields that previously had their own
 * partly-overlapping option sets: litigation plaintiff (`entity_type`) and defendant
 * (`sme_annotation`), PTAB patent owner (`up_po_type`) and petitioner (`up_pet_type`).
 *
 * `OC` records that size was never asked (the patent-owner fields have no size option),
 * so it is not a third size between `OC_L` and `OC_SM`. `NPE` and `O` carry no rows in
 * either system and are not offered as filter options, but stay here so a stray stored
 * value still renders as text.
 */
export const ENTITY_TYPE_MAP = {
  OC_L: "Operating Company (Large)",
  OC_SM: "Operating Company (Small/Medium)",
  OC: "Operating Company (Unknown Size)",
  NPE_PAE: "NPE (Patent Assertion Entity)",
  NPE_SC: "NPE (Small Company)",
  NPE_I: "NPE (Individual)",
  NPE: "NPE (Unspecified)",
  U: "University / Research Institute",
  G: "Government / NGO / Non-Profit",
  DA: "Defensive Aggregator",
  UP: "Unified Patents",
  O: "Other"
};

// Codes reach us in mixed case: raw index values are lowercased, the PTAB fetchers
// uppercase their aggregation buckets, and `toTitleCase` in the litigation chart fetcher
// turns `OC_L` into `Oc_l`. Normalize before lookup so one renderer covers every path.
export const renderEntityType = value => (value ? ENTITY_TYPE_MAP[String(value).toUpperCase()] || value : value);

/**
 * Tag colour per entity family, mirroring the chart palette: blue = NPE, grey = operating
 * company, green = institutional, orange = aggregator.
 *
 * Charts need a distinct shade per member because they draw several at once; a tag shows one
 * value with its label right beside it, so it only has to signal the family. These are Ant
 * Design presets rather than the raw shade hexes, which keeps text contrast handled — the
 * lighter shades (grey100 is #F2F2F2) are unreadable behind white tag text.
 */
export const ENTITY_TYPE_TAG_COLOR_MAP = {
  NPE_PAE: "blue",
  NPE_SC: "blue",
  NPE_I: "blue",
  NPE: "blue",
  OC_L: "default",
  OC_SM: "default",
  OC: "default",
  U: "green",
  G: "green",
  DA: "orange",
  UP: "orange",
  O: "default"
};

export const renderEntityTypeTagColor = value => (value ? ENTITY_TYPE_TAG_COLOR_MAP[String(value).toUpperCase()] || "default" : "default");

// Entity-type filters go into the URL as quoted codes (`toExactMatchQueryString`), so the
// applied-filter chips would otherwise read `"NPE_PAE"` instead of the label the select and
// the table show. Strip the exact-match quotes, then label each code.
export const renderEntityTypeQuery = value => {
  const codes = fromExactMatchQueryString(value);
  return isArray(codes) ? codes.map(renderEntityType).join(", ") : renderEntityType(codes);
};
